import { DiffEditor, Editor } from "@monaco-editor/react";
import { process } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Window } from "@progress/kendo-react-dialogs";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import {
  GridColumn as Column,
  Grid,
  GridColumnMenuGroup,
  GridToolbar,
} from "@progress/kendo-react-grid";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import LoadingScreen from "../../Pages/LoadingScreen/LoadingScreen";
import { showTemplateHistoryWindow, templateData } from "../NewTemplateSignals";
import {
  templateHasMore,
  templateHistoryData,
  templateHistoryError,
  templateHistoryStatus,
} from "./TemplateHistorySignals";
import {
  clearTemplateHistory,
  getTemplateHistory,
  loadMoreTemplateHistory,
} from "./TemplateHistoryUtils";

const OldValueCell = ({ dataItem, onShowDetails, tdProps }) => (
  <td {...tdProps}>
    {dataItem.oldValue && (
      <Button
        type="button"
        onClick={() => onShowDetails(dataItem)}
        className="!k-ml-4"
      >
        Show Details
      </Button>
    )}
  </td>
);

OldValueCell.propTypes = {
  dataItem: PropTypes.shape({
    oldValue: PropTypes.string,
  }).isRequired,
  onShowDetails: PropTypes.func.isRequired,
  tdProps: PropTypes.object,
};

const NewValueCell = ({ dataItem, onShowDetails, tdProps }) => (
  <td {...tdProps}>
    {dataItem.newValue && (
      <Button
        type="button"
        onClick={() => onShowDetails(dataItem)}
        className="!k-ml-4"
      >
        Show Details
      </Button>
    )}
  </td>
);

NewValueCell.propTypes = {
  dataItem: PropTypes.shape({
    newValue: PropTypes.string,
  }).isRequired,
  onShowDetails: PropTypes.func.isRequired,
  tdProps: PropTypes.object,
};

const DiffCell = ({ dataItem, onShowDetails }) => (
  <td>
    {dataItem.actionType === "UPDATE" && (
      <Button
        type="button"
        onClick={() => onShowDetails(dataItem)}
        className="!k-ml-4"
      >
        Show Details
      </Button>
    )}
  </td>
);

DiffCell.propTypes = {
  dataItem: PropTypes.shape({
    actionType: PropTypes.string,
  }).isRequired,
  onShowDetails: PropTypes.func.isRequired,
};

// Factory functions to create cell renderers
const createOldValueCellRenderer = (onShowDetails) => {
  const OldValueCellRenderer = (event) => (
    <OldValueCell
      dataItem={event.dataItem}
      onShowDetails={onShowDetails}
      tdProps={event.tdProps}
    />
  );
  OldValueCellRenderer.displayName = 'OldValueCellRenderer';
  return OldValueCellRenderer;
};

const createNewValueCellRenderer = (onShowDetails) => {
  const NewValueCellRenderer = (event) => (
    <NewValueCell
      dataItem={event.dataItem}
      onShowDetails={onShowDetails}
      tdProps={event.tdProps}
    />
  );
  NewValueCellRenderer.displayName = 'NewValueCellRenderer';
  return NewValueCellRenderer;
};

const createDiffCellRenderer = (onShowDetails) => {
  const DiffCellRenderer = (event) => (
    <DiffCell
      dataItem={event.dataItem}
      onShowDetails={onShowDetails}
    />
  );
  DiffCellRenderer.displayName = 'DiffCellRenderer';
  return DiffCellRenderer;
};

export const TemplateAuditHistory = () => {
  const excelExportComponent = useRef(null);
  const gridContainerRef = useRef(null);
  const pageSize = 15;

  const [selectedOldValueItem, setSelectedOldValueItem] = useState(null);
  const [selectedNewValueItem, setSelectedNewValueItem] = useState(null);
  const [selectedDiffItem, setSelectedDiffItem] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [updateCounter, setUpdateCounter] = useState(0);

  const [dataState, setDataState] = useState({
    take: pageSize,
    skip: 0,
  });

  // Load template history when window opens
  useEffect(() => {
    if (showTemplateHistoryWindow.value && templateData.value?.id) {
      clearTemplateHistory();
      getTemplateHistory(templateData.value.id);
    }
  }, [showTemplateHistoryWindow.value]);

  // Force re-render when signal changes
  useEffect(() => {
    setUpdateCounter((prev) => prev + 1);
  }, [templateHistoryData.value]);

  // Force re-render when templateHasMore changes
  useEffect(() => {
    setUpdateCounter((prev) => prev + 1);
  }, [templateHasMore.value]);

  // Get data directly from signal
  const filteredData = templateHistoryData.value || [];

  useEffect(() => {
    const handleScroll = () => {
      if (!gridContainerRef.current) return;

      const container = gridContainerRef.current;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 100) {
        handleLoadMore();
      }
    };

    const gridContainer = gridContainerRef.current;
    if (gridContainer) {
      gridContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (gridContainer) {
        gridContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [templateHasMore.value, isLoadingMore]);

  const processedData = React.useMemo(() => {
    return process(filteredData ?? [], dataState);
  }, [filteredData, dataState, updateCounter]);

  const handleLoadMore = async () => {
    if (templateHasMore.value && !isLoadingMore && templateData.value?.id) {
      setIsLoadingMore(true);
      try {
        await loadMoreTemplateHistory(templateData.value.id);
      } catch (error) {
        console.error("Error loading more data:", error);
      } finally {
        setIsLoadingMore(false);
      }
    } else {
      console.log("Conditions not met for loading more data");
    }
  };

  const handleManualLoadMore = () => {
    handleLoadMore();
  };

  const exportToExcel = () => {
    if (excelExportComponent.current) {
      excelExportComponent.current.save();
    }
  };

  const dataStateChange = (event) => {
    setDataState(event.dataState);
  };

  const handleClose = () => {
    showTemplateHistoryWindow.value = false;
    setTimeout(() => {
      clearTemplateHistory();
    }, 300);
  };

  const showLoadingScreen = templateHistoryStatus.value === "loading" && filteredData.length === 0;

  if (!showTemplateHistoryWindow.value) return null;

  return (
    <>
      <Window
        title={`Template History - ${templateData.value?.templateName}`}
        onClose={handleClose}
        initialHeight={650}
        initialWidth={1200}
        modal={true}
      >
        <div>
          {showLoadingScreen ? (
            <div>
              <LoadingScreen />
            </div>
          ) : (
            <div
              className="grid-container"
              ref={gridContainerRef}
              style={{ maxHeight: "500px", overflow: "auto" }}
            >
              <ExcelExport
                data={processedData.data}
                ref={excelExportComponent}
              >
                <Grid
                  data={processedData}
                  onDataStateChange={dataStateChange}
                  pageable={true}
                  pageSize={pageSize}
                  resizable={true}
                  reorderable={true}
                  sortable={true}
                >
                  <GridToolbar>
                    <Button
                      title="Export to Excel"
                      onClick={exportToExcel}
                      style={{
                        position: "absolute",
                        right: 5,
                        bottom: 7,
                      }}
                    >
                      Export to Excel
                    </Button>
                  </GridToolbar>

                  <Column
                    field="timestamp"
                    title="Timestamp"
                    filter="text"
                    sortable
                    cells={{
                      data: renderColumnTimestamp,
                    }}
                  />

                  <Column
                    field="userId"
                    title="User ID"
                    filter="text"
                    columnMenu={ColumnMenu}
                  />
                  <Column
                    field="actionType"
                    title="Action Type"
                    filter="text"
                    filterable={true}
                    columnMenu={ColumnMenu}
                  />
                  <Column
                    field="oldValue"
                    title="Old Value"
                    cells={{
                      data: createOldValueCellRenderer(setSelectedOldValueItem),
                    }}
                  />

                  <Column
                    field="newValue"
                    title="New Value"
                    cells={{
                      data: createNewValueCellRenderer(setSelectedNewValueItem),
                    }}
                  />

                  <Column
                    field="diff"
                    title="Diff"
                    cells={{
                      data: createDiffCellRenderer(setSelectedDiffItem),
                    }}
                  />
                </Grid>
              </ExcelExport>
            </div>
          )}

          {templateHistoryError.value && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              Error: {templateHistoryError.value}
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "5px",
            }}
          >
            {(isLoadingMore ||
              (templateHistoryStatus.value === "loading" && filteredData.length > 0)) && (
              <div className="text-center py-2">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span className="ml-2">Loading more data...</span>
              </div>
            )}

            {!templateHasMore.value && filteredData.length > 0 && (
              <div className="text-center py-2 text-gray-500">
                All data has been loaded ({filteredData.length} items)
              </div>
            )}

            {templateHasMore.value && !isLoadingMore && (
              <div className="text-center py-2">
                <Button
                  onClick={handleManualLoadMore}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  Load More Data (100 items)
                </Button>
              </div>
            )}
          </div>
        </div>
      </Window>

      {/* Detail Windows - Outside main modal */}
      {selectedOldValueItem && (
        <Window
          title="Old Value Details"
          onClose={() => setSelectedOldValueItem(null)}
          initialHeight={650}
          initialWidth={1000}
        >
          <Editor
            height="100%"
            language="json"
            value={JSON.stringify(JSON.tryParse(selectedOldValueItem.oldValue, "{}"), null, 2)}
            options={{
              readOnly: true,
              minimap: { enabled: true },
            }}
            loading={<div>Loading...</div>}
          />
        </Window>
      )}

      {selectedNewValueItem && (
        <Window
          title="New Value Details"
          onClose={() => setSelectedNewValueItem(null)}
          initialHeight={650}
          initialWidth={1000}
        >
          <Editor
            height="100%"
            language="json"
            value={JSON.stringify(JSON.tryParse(selectedNewValueItem.newValue, "{}"), null, 2)}
            options={{
              readOnly: true,
              minimap: { enabled: true },
            }}
            loading={<div>Loading...</div>}
          />
        </Window>
      )}

      {selectedDiffItem && (
        <Window
          title="Diff Details"
          onClose={() => setSelectedDiffItem(null)}
          initialHeight={650}
          initialWidth={1000}
        >
          <DiffEditor
            height="100%"
            language="json"
            original={JSON.stringify(JSON.tryParse(selectedDiffItem.oldValue, {}), null, 2)}
            modified={JSON.stringify(JSON.tryParse(selectedDiffItem.newValue, {}), null, 2)}
            keepCurrentOriginalModel={true}
            keepCurrentModifiedModel={true}
            options={{
              readOnly: true,
              renderSideBySide: true,
              automaticLayout: true,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
            }}
            loading={<div>Loading...</div>}
          />
        </Window>
      )}
    </>
  );
};

const ColumnMenu = (event) => {
  return (
    <div>
      <GridColumnMenuGroup {...event} />
    </div>
  );
};

ColumnMenu.propTypes = {
  event: PropTypes.object,
};

const renderColumnTimestamp = (event) => {
  return (
    <td>
      {new Date(event.dataItem.timestamp).toLocaleString("en-US", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })}
    </td>
  );
};

renderColumnTimestamp.propTypes = {
  dataItem: PropTypes.shape({
    timestamp: PropTypes.string,
  }),
};
